import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  X, Send, ExternalLink, Mic, MicOff, LayoutGrid, User, Briefcase,
  MapPin, Calendar, Hash, Loader2, Receipt, BarChart3,
} from 'lucide-react';
import TripFormInBot from './TripFormInBot';
import BidFormInBot, { BidSuccessCard } from './BidFormInBot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WA_SESSION_KEY = 'horsego_wa_session_id';

// Proper WhatsApp brand glyph.
const WhatsAppIcon = ({ className = 'w-6 h-6' }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.003 3.2c-7.06 0-12.79 5.73-12.79 12.79 0 2.253.59 4.457 1.712 6.4L3.2 28.8l6.57-1.717a12.74 12.74 0 006.233 1.588h.005c7.06 0 12.79-5.73 12.79-12.79 0-3.417-1.33-6.63-3.746-9.047A12.7 12.7 0 0016.003 3.2zm0 23.36h-.004a10.57 10.57 0 01-5.386-1.475l-.386-.23-3.9 1.023 1.04-3.8-.251-.4a10.56 10.56 0 01-1.62-5.638c0-5.867 4.775-10.64 10.65-10.64 2.844 0 5.516 1.108 7.525 3.12a10.57 10.57 0 013.117 7.527c0 5.867-4.775 10.64-10.65 10.64zm5.84-7.964c-.32-.16-1.894-.935-2.188-1.042-.294-.107-.508-.16-.722.16-.213.32-.828 1.042-1.015 1.256-.187.213-.374.24-.694.08-.32-.16-1.352-.498-2.576-1.59-.952-.85-1.595-1.9-1.782-2.22-.187-.32-.02-.492.14-.652.144-.143.32-.374.48-.56.16-.187.213-.32.32-.534.107-.213.053-.4-.027-.56-.08-.16-.722-1.74-.99-2.384-.26-.625-.525-.54-.722-.55l-.615-.01c-.213 0-.56.08-.853.4-.294.32-1.12 1.095-1.12 2.67 0 1.575 1.146 3.098 1.306 3.312.16.213 2.255 3.442 5.463 4.826.763.33 1.36.526 1.824.674.766.243 1.464.209 2.016.127.615-.092 1.894-.775 2.162-1.523.267-.747.267-1.388.187-1.523-.08-.133-.294-.213-.614-.373z"/>
  </svg>
);

const readCustomer = () => {
  try { return JSON.parse(sessionStorage.getItem('customer') || 'null'); } catch { return null; }
};
const readTransporter = () => {
  try { return JSON.parse(sessionStorage.getItem('transporter') || 'null'); } catch { return null; }
};

/**
 * In-page WhatsApp-styled chat panel (desktop). Mirrors the Ask-HorseGO web
 * assistant: same 4 options, the same customer login + TripFormInBot for
 * "Get a Quote", and the same transporter login + browse-open-requests +
 * BidFormInBot for "For Transporters".
 */
const WhatsAppPanel = ({ open, onClose, waDeepLink }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [intent, setIntent] = useState('');
  const [showOptions, setShowOptions] = useState(true);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(WA_SESSION_KEY) || null);

  // Inline auth (customer for Get-a-Quote, transporter for For-Transporters).
  const [needLogin, setNeedLogin] = useState(false);
  const [authRole, setAuthRole] = useState('customer');
  const [authForm, setAuthForm] = useState({ email: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authTick, setAuthTick] = useState(0);

  // Transporter bidding-from-panel state (mirrors ChatWidget).
  const [bidView, setBidView] = useState(null); // null | 'list' | 'form' | 'success' | 'transactions' | 'dashboard'
  const [bidRequests, setBidRequests] = useState([]);
  const [bidLoading, setBidLoading] = useState(false);
  const [bidLoadError, setBidLoadError] = useState('');
  const [bidSelected, setBidSelected] = useState(null);
  const [bidSuccess, setBidSuccess] = useState(null);
  // Dashboard summaries (mirror web dashboard key indicators).
  const [trDash, setTrDash] = useState(null);
  const [trDashLoading, setTrDashLoading] = useState(false);
  const [custDash, setCustDash] = useState(null);
  const [custDashLoading, setCustDashLoading] = useState(false);
  // Transporter: my transactions (submitted quotes + statuses).
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState('');

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);

  const customer = readCustomer();
  const isAuthenticated = !!customer?.email;
  const transporter = readTransporter();
  const isTransporter = !!transporter?.user_id;

  const SpeechRecognition = (typeof window !== 'undefined')
    && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const voiceSupported = !!SpeechRecognition;
  const langForRecognition = () => (
    { fr: 'fr-FR', en: 'en-US', es: 'es-ES', nl: 'nl-NL', ar: 'ar-MA' }[(i18n.language || 'en').slice(0, 2)] || 'en-US'
  );

  const toggleMic = () => {
    if (listening) {
      try { recognitionRef.current?.stop(); } catch { /* noop */ }
      setListening(false);
      return;
    }
    if (!voiceSupported) return;
    const recognition = new SpeechRecognition();
    recognition.lang = langForRecognition();
    recognition.interimResults = true;
    recognition.continuous = false;
    let finalT = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const txt = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalT += txt; else interim += txt;
      }
      setInput((finalT + interim).trim());
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => {
      setListening(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    };
    try {
      recognition.start();
      recognitionRef.current = recognition;
      setListening(true);
    } catch { /* noop */ }
  };

  const options = [
    { intent: 'get_quote', emoji: '📝', label: t('chat.modes.getQuote') || 'Get a Quote' },
    { intent: 'track_booking', emoji: '🔎', label: t('chat.modes.track') || 'Track a Booking' },
    { intent: 'transporter', emoji: '🚚', label: t('chat.modes.transporter') || 'For Transporters' },
    { intent: 'my_dashboard', emoji: '📊', label: t('wa.myDashboard', 'My Dashboard') },
    { intent: 'general', emoji: '💬', label: t('chat.modes.askQuestion') || 'Ask a Question' },
  ];

  const welcomeText = t('wa.welcome', "👋 Welcome to HorseGO — Morocco's #1 horse transport platform.\nHow can I help you today?");

  const resetBid = () => {
    setBidView(null); setBidRequests([]); setBidLoadError('');
    setBidSelected(null); setBidSuccess(null);
  };

  // Reset the panel back to the welcome menu — available anytime.
  const backToMenu = () => {
    setIntent('');
    setNeedLogin(false);
    setAuthError('');
    setInput('');
    resetBid();
    setMessages([{ role: 'assistant', text: welcomeText }]);
    setShowOptions(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', text: welcomeText }]);
      setShowOptions(true);
    }
    if (open) setTimeout(() => inputRef.current?.focus(), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const callBackend = async (text, intentToUse) => {
    setSending(true);
    const tr = readTransporter();
    try {
      const { data } = await axios.post(`${API}/chat/message`, {
        session_id: sessionId,
        message: text,
        language: i18n.language || 'auto',
        mode: 'whatsapp',
        intent: intentToUse || intent || '',
        is_authenticated: isAuthenticated,
        is_transporter: !!tr?.user_id,
        customer: customer || null,
        transporter: tr || null,
        customer_email: customer?.email || null,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(WA_SESSION_KEY, data.session_id);
      }
      setMessages((m) => [...m, { role: 'assistant', text: data.reply || '…' }]);
    } catch {
      setMessages((m) => [...m, {
        role: 'assistant',
        text: t('wa.error', 'Sorry, something went wrong. Please try again or open WhatsApp directly.'),
      }]);
    } finally {
      setSending(false);
    }
  };

  // Transporter: fetch the 5 newest open requests to bid on (same endpoint as ChatWidget).
  const openBidPicker = async () => {
    const tr = readTransporter();
    if (!tr?.user_id) return;
    setBidView('list');
    setBidSelected(null);
    setBidSuccess(null);
    setBidLoadError('');
    setBidLoading(true);
    try {
      const { data } = await axios.get(`${API}/chat/transporter/open-requests`, {
        params: { transporter_user_id: tr.user_id, limit: 20 },
      });
      setBidRequests(Array.isArray(data) ? data : []);
    } catch {
      setBidLoadError(t('chat.bid.loadError') || 'Could not load open requests. Please try again.');
      setBidRequests([]);
    } finally {
      setBidLoading(false);
    }
  };

  // Transporter: load my submitted quotes + their statuses.
  const openTransactions = async () => {
    const tr = readTransporter();
    if (!tr?.user_id) return;
    setBidView('transactions');
    setBidSelected(null);
    setBidSuccess(null);
    setTxError('');
    setTxLoading(true);
    try {
      const { data } = await axios.get(`${API}/quotes/transporter/${tr.user_id}`);
      setTransactions(Array.isArray(data) ? data : []);
    } catch {
      setTxError(t('wa.txLoadError', 'Could not load your transactions. Please try again.'));
      setTransactions([]);
    } finally {
      setTxLoading(false);
    }
  };

  // Transporter: load web-dashboard key indicators (summary).
  const loadTransporterDashboard = async () => {
    const tr = readTransporter();
    if (!tr?.user_id) return;
    setBidView('dashboard');
    setBidSelected(null);
    setBidSuccess(null);
    setTrDashLoading(true);
    try {
      const { data } = await axios.get(`${API}/transporters/${tr.user_id}/dashboard-summary`);
      setTrDash(data);
    } catch {
      setTrDash(null);
    } finally {
      setTrDashLoading(false);
    }
  };

  // Customer: load web-dashboard key indicators (summary).
  const loadCustomerDashboard = async () => {
    const cust = readCustomer();
    if (!cust?.email) return;
    setCustDashLoading(true);
    try {
      const { data } = await axios.get(`${API}/customer/dashboard-summary`, { params: { email: cust.email } });
      setCustDash(data);
    } catch {
      setCustDash(null);
    } finally {
      setCustDashLoading(false);
    }
  };

  const pickOption = (opt) => {
    if (sending) return;
    setIntent(opt.intent);
    setShowOptions(false);

    if (opt.intent === 'get_quote') {
      if (!isAuthenticated) {
        setAuthRole('customer');
        setNeedLogin(true);
        setAuthError('');
        setAuthForm({ email: '', password: '' });
      }
      return; // login form or trip form takes over
    }

    if (opt.intent === 'my_dashboard') {
      if (!isAuthenticated) {
        setAuthRole('customer');
        setNeedLogin(true);
        setAuthError('');
        setAuthForm({ email: '', password: '' });
      } else {
        loadCustomerDashboard();
      }
      return;
    }

    if (opt.intent === 'transporter') {
      if (!isTransporter) {
        setAuthRole('transporter');
        setNeedLogin(true);
        setAuthError('');
        setAuthForm({ email: '', password: '' });
      } else {
        openBidPicker(); // straight to browse open requests
      }
      return;
    }

    // track_booking / general → conversational
    setMessages((m) => [...m, { role: 'user', text: `${opt.emoji} ${opt.label}` }]);
    callBackend(opt.label, opt.intent);
  };

  const send = () => {
    const text = input.trim();
    if (!text || sending) return;
    if (text.toLowerCase() === 'menu') { setInput(''); backToMenu(); return; }
    setInput('');
    setShowOptions(false);
    setMessages((m) => [...m, { role: 'user', text }]);
    callBackend(text, intent || 'general');
  };

  const submitLogin = async (e) => {
    if (e) e.preventDefault();
    if (authLoading) return;
    setAuthError('');
    setAuthLoading(true);
    try {
      if (authRole === 'transporter') {
        const { data } = await axios.post(`${API}/transporters/login`, {
          login: authForm.email.trim().toLowerCase(),
          password: authForm.password,
        });
        sessionStorage.setItem('transporter', JSON.stringify(data.transporter));
        setNeedLogin(false);
        setAuthTick((n) => n + 1);
        openBidPicker();
      } else {
        const { data } = await axios.post(`${API}/customers/login`, {
          email: authForm.email.trim().toLowerCase(),
          password: authForm.password,
        });
        sessionStorage.setItem('customer', JSON.stringify({
          session_token: data.session_token,
          customer_id: data.customer_id,
          first_name: data.first_name,
          last_name: data.last_name,
          email: data.email,
          phone: data.phone || '',
          city: data.city || '',
        }));
        setNeedLogin(false);
        setAuthTick((n) => n + 1);
        if (intent === 'my_dashboard') loadCustomerDashboard();
      }
    } catch (err) {
      setAuthError(err.response?.data?.detail || t('wa.loginFailed', 'Login failed. Please check your credentials.'));
    } finally {
      setAuthLoading(false);
    }
  };

  if (!open) return null;

  const showLogin = needLogin && (intent === 'get_quote' || intent === 'transporter' || intent === 'my_dashboard');
  const showTripForm = intent === 'get_quote' && isAuthenticated && !needLogin;
  const showTransporter = intent === 'transporter' && isTransporter && !needLogin;
  const showCustomerDashboard = intent === 'my_dashboard' && isAuthenticated && !needLogin;
  const isTransporterAuth = authRole === 'transporter';

  return (
    <div
      data-testid="wa-panel"
      className="fixed bottom-6 right-6 z-[61] w-[calc(100vw-3rem)] sm:w-[380px] h-[600px] max-h-[85vh] rounded-2xl shadow-2xl shadow-black/60 flex flex-col overflow-hidden border border-black/10"
    >
      {/* Header (WhatsApp green) */}
      <div className="px-4 py-3 bg-[#075E54] flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#25D366] flex items-center justify-center text-white">
            <WhatsAppIcon className="w-6 h-6" />
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-tight">HorseGO</p>
            <p className="text-emerald-200 text-xs flex items-center gap-1">
              <span className="w-1.5 h-1.5 bg-emerald-300 rounded-full animate-pulse" />
              {t('wa.online', 'WhatsApp • online')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={backToMenu}
            data-testid="wa-menu-btn"
            title={t('wa.menu', 'Back to menu')}
            className="flex items-center gap-1 px-2 h-8 rounded-full hover:bg-white/15 text-white/90 text-xs font-semibold"
          >
            <LayoutGrid className="w-4 h-4" />
            <span className="hidden sm:inline">{t('wa.menuShort', 'Menu')}</span>
          </button>
          <a
            href={waDeepLink || 'https://wa.me/212626007465'}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="wa-open-app-link"
            title={t('wa.openApp', 'Open in WhatsApp app')}
            className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center text-white/90"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          <button
            onClick={onClose}
            aria-label="Close WhatsApp chat"
            data-testid="wa-close-btn"
            className="w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center text-white/90"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* === Inline login (customer for Get-a-Quote, transporter for For-Transporters) === */}
      {showLogin && (
        <div className="flex-1 overflow-y-auto px-5 py-6 bg-[#ECE5DD]" data-testid="wa-login">
          <div className="mb-5 flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#075E54] flex items-center justify-center flex-shrink-0">
              {isTransporterAuth ? <Briefcase className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h3 className="text-gray-900 font-bold text-base leading-tight mb-1">
                {isTransporterAuth
                  ? t('wa.transporterLoginTitle', 'Transporter sign in')
                  : t('wa.loginTitle', 'Sign in to request a quote')}
              </h3>
              <p className="text-gray-600 text-sm">
                {isTransporterAuth
                  ? t('wa.transporterLoginSubtitle', 'Connect your transporter account to browse open requests and submit quotes.')
                  : t('wa.loginSubtitle', 'Please connect to your HorseGO account before creating a transport request.')}
              </p>
            </div>
          </div>

          <form onSubmit={submitLogin} className="space-y-3" data-testid="wa-login-form">
            <div>
              <label className="block text-gray-700 text-xs font-medium mb-1.5">
                {t('chat.auth.email', 'Email')}
              </label>
              <input
                type="email"
                required
                autoComplete="email"
                value={authForm.email}
                onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                placeholder="you@example.com"
                disabled={authLoading}
                data-testid="wa-login-email"
                className="w-full bg-white border border-black/10 focus:border-[#25D366] focus:outline-none rounded-lg px-3 py-2.5 text-gray-900 text-sm placeholder:text-gray-400"
              />
            </div>
            <div>
              <label className="block text-gray-700 text-xs font-medium mb-1.5">
                {t('chat.auth.password', 'Password')}
              </label>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={authForm.password}
                onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                placeholder="••••••••"
                disabled={authLoading}
                data-testid="wa-login-password"
                className="w-full bg-white border border-black/10 focus:border-[#25D366] focus:outline-none rounded-lg px-3 py-2.5 text-gray-900 text-sm placeholder:text-gray-400"
              />
            </div>

            {authError && (
              <div role="alert" data-testid="wa-login-error"
                className="px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs">
                {authError}
              </div>
            )}

            <button
              type="submit"
              disabled={authLoading || !authForm.email || !authForm.password}
              data-testid="wa-login-submit"
              className="w-full bg-[#25D366] hover:bg-[#20bd5a] disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-full py-2.5 text-sm transition-colors"
            >
              {authLoading ? t('chat.auth.signingIn', 'Signing in…') : t('chat.auth.signIn', 'Sign in')}
            </button>
          </form>

          <div className="mt-4 pt-4 border-t border-black/10 text-center">
            <p className="text-gray-600 text-xs mb-2">
              {isTransporterAuth
                ? t('chat.auth.noTransporterAccount', 'Not a transporter yet?')
                : t('chat.auth.noAccount', "Don't have an account yet?")}
            </p>
            <button
              onClick={() => { onClose && onClose(); navigate(isTransporterAuth ? '/drivers' : '/customer/register'); }}
              data-testid="wa-login-signup"
              className="text-[#075E54] hover:text-[#25D366] text-sm font-semibold transition-colors"
            >
              {isTransporterAuth
                ? t('chat.cta.transporterRegister', 'Become a transporter →')
                : t('chat.auth.createAccount', 'Create a free account →')}
            </button>
          </div>
        </div>
      )}

      {/* === In-bot trip form (authenticated Get a Quote) === */}
      {showTripForm && (
        <TripFormInBot
          key={authTick}
          customer={customer}
          language={i18n.language?.slice(0, 2) || 'en'}
          t={t}
          onCreated={(requestId, fromButton) => {
            if (fromButton) { onClose && onClose(); navigate('/dashboard'); }
          }}
        />
      )}

      {/* === Transporter: browse open requests → bid → success (mirrors AskHorseGO) === */}
      {showTransporter && (
        <>
          {!bidView && (
            <div className="flex-1 overflow-y-auto px-5 py-6 bg-[#ECE5DD]" data-testid="wa-transporter-home">
              <div className="mb-4 flex items-center gap-2 text-gray-700 text-sm">
                <Briefcase className="w-4 h-4 text-[#075E54]" />
                <span className="truncate font-medium">{transporter?.company_name || transporter?.email}</span>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                {t('wa.transporterHome', 'Welcome back! Browse the newest open transport requests and submit your quote.')}
              </p>
              <button
                type="button"
                onClick={openBidPicker}
                data-testid="wa-open-bid-picker-btn"
                className="w-full bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-full py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Briefcase className="w-4 h-4" />
                {t('chat.bid.browseRequests', 'Browse open requests')}
              </button>
              <button
                type="button"
                onClick={openTransactions}
                data-testid="wa-open-transactions-btn"
                className="mt-3 w-full bg-white border border-[#25D366]/50 text-[#075E54] hover:bg-[#25D366]/10 font-bold rounded-full py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
              >
                <Receipt className="w-4 h-4" />
                {t('wa.myTransactions', 'My transactions')}
              </button>
              <button
                type="button"
                onClick={loadTransporterDashboard}
                data-testid="wa-open-dashboard-btn"
                className="mt-3 w-full bg-white border border-[#075E54]/40 text-[#075E54] hover:bg-[#075E54]/10 font-bold rounded-full py-2.5 text-sm transition-colors flex items-center justify-center gap-2"
              >
                <BarChart3 className="w-4 h-4" />
                {t('wa.myDashboard', 'My Dashboard')}
              </button>
              <button
                type="button"
                onClick={() => { onClose && onClose(); navigate('/drivers/dashboard'); }}
                data-testid="wa-transporter-dashboard-btn"
                className="mt-3 w-full text-[#075E54] hover:text-[#25D366] text-sm font-semibold"
              >
                {t('chat.cta.transporterDashboard', 'Go to my dashboard →')}
              </button>
            </div>
          )}

          {bidView === 'list' && (
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#ECE5DD]" data-testid="wa-bid-list">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  {t('chat.bid.openRequestsTitle', 'Open trip requests')}
                </h4>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={openTransactions}
                    data-testid="wa-goto-transactions-btn"
                    className="text-xs font-semibold text-[#075E54] hover:text-[#25D366] flex items-center gap-1"
                  >
                    <Receipt className="w-3.5 h-3.5" /> {t('wa.myTransactions', 'My transactions')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBidView(null)}
                    data-testid="wa-bid-close-btn"
                    className="text-xs text-gray-500 hover:text-gray-800"
                  >
                    × {t('chat.bid.close', 'Close')}
                  </button>
                </div>
              </div>
              {bidLoading && (
                <div className="flex items-center justify-center py-6 text-gray-500 text-xs gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('chat.bid.loading', 'Loading requests…')}
                </div>
              )}
              {!bidLoading && bidLoadError && (
                <div data-testid="wa-bid-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {bidLoadError}
                </div>
              )}
              {!bidLoading && !bidLoadError && bidRequests.length === 0 && (
                <div data-testid="wa-bid-empty" className="text-xs text-gray-500 bg-white border border-black/10 rounded-lg px-3 py-4 text-center">
                  {t('chat.bid.empty', 'No open requests right now — check back soon!')}
                </div>
              )}
              {!bidLoading && bidRequests.map((r) => {
                const mapUrl = `${API}/maps/static-map?origin=${encodeURIComponent(r.pickup_address)}&destination=${encodeURIComponent(r.dropoff_address)}&width=360&height=120`;
                return (
                  <button
                    type="button"
                    key={r.request_id}
                    onClick={() => { setBidSelected(r); setBidView('form'); }}
                    data-testid={`wa-bid-card-${r.request_id}`}
                    className="w-full text-left rounded-xl border border-black/10 bg-white hover:border-[#25D366]/60 overflow-hidden transition-colors shadow-sm"
                  >
                    <img src={mapUrl} alt="" className="w-full h-24 object-cover bg-gray-100"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <div className="p-3 space-y-1.5 text-xs">
                      <div className="text-[#075E54] font-mono text-[10px]">{r.request_id}</div>
                      <div className="flex items-start gap-1.5 text-gray-900">
                        <MapPin className="w-3.5 h-3.5 text-[#25D366] mt-0.5 flex-shrink-0" />
                        <div className="leading-snug flex-1 min-w-0">
                          <div className="truncate">{r.pickup_address}</div>
                          <div className="text-gray-500 truncate">→ {r.dropoff_address}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 text-gray-600 pt-0.5">
                        {r.transport_date && (
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {r.transport_date}</span>
                        )}
                        {r.horse_count && (
                          <span className="flex items-center gap-1"><Hash className="w-3 h-3" /> {r.horse_count}</span>
                        )}
                      </div>
                      <div className="pt-1 text-[#075E54] font-semibold text-[11px]">
                        {t('chat.bid.tapToBid', 'Tap to submit a bid →')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {bidView === 'form' && bidSelected && (
            <BidFormInBot
              request={bidSelected}
              transporterUserId={transporter.user_id}
              onBack={() => { setBidSelected(null); setBidView('list'); }}
              onSubmitted={(quote) => { setBidSuccess(quote); setBidView('success'); }}
            />
          )}

          {bidView === 'success' && (
            <div className="flex-1 overflow-y-auto px-3 py-3 bg-[#ECE5DD]">
              <BidSuccessCard
                quote={bidSuccess}
                onDone={() => { setBidSuccess(null); openBidPicker(); }}
              />
            </div>
          )}

          {bidView === 'transactions' && (
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-[#ECE5DD]" data-testid="wa-transactions-list">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <Receipt className="w-4 h-4 text-[#075E54]" />
                  {t('wa.myTransactions', 'My transactions')}
                </h4>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={openBidPicker}
                    data-testid="wa-goto-browse-btn"
                    className="text-xs font-semibold text-[#075E54] hover:text-[#25D366] flex items-center gap-1"
                  >
                    <Briefcase className="w-3.5 h-3.5" /> {t('chat.bid.browseRequests', 'Browse open requests')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBidView(null)}
                    data-testid="wa-transactions-close-btn"
                    className="text-xs text-gray-500 hover:text-gray-800"
                  >
                    × {t('chat.bid.close', 'Close')}
                  </button>
                </div>
              </div>
              {txLoading && (
                <div className="flex items-center justify-center py-6 text-gray-500 text-xs gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('wa.txLoading', 'Loading transactions…')}
                </div>
              )}
              {!txLoading && txError && (
                <div data-testid="wa-transactions-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {txError}
                </div>
              )}
              {!txLoading && !txError && transactions.length === 0 && (
                <div data-testid="wa-transactions-empty" className="text-xs text-gray-500 bg-white border border-black/10 rounded-lg px-3 py-4 text-center">
                  {t('wa.txEmpty', 'You have no transactions yet.')}
                </div>
              )}
              {!txLoading && transactions.map((q) => {
                const st = (q.status || 'pending').toLowerCase();
                const badge = {
                  pending: 'bg-amber-100 text-amber-700',
                  accepted: 'bg-emerald-100 text-emerald-700',
                  paid: 'bg-emerald-600 text-white',
                  completed: 'bg-sky-100 text-sky-700',
                  rejected: 'bg-red-100 text-red-700',
                  expired: 'bg-gray-200 text-gray-600',
                  withdrawn: 'bg-gray-200 text-gray-600',
                }[st] || 'bg-gray-100 text-gray-600';
                return (
                  <div
                    key={q.quote_id || q.trip_request_id}
                    data-testid={`wa-transaction-${q.quote_id || q.trip_request_id}`}
                    className="rounded-xl border border-black/10 bg-white px-3 py-2.5 text-xs shadow-sm"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[#075E54] font-mono text-[10px]">{q.trip_request_id}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge}`}>{st}</span>
                    </div>
                    <div className="mt-1 flex items-center justify-between text-gray-800">
                      <span className="font-semibold">
                        {Math.round(Number(q.transport_price || q.base_price || 0))} MAD
                      </span>
                      {q.service_type && (
                        <span className="text-gray-500 capitalize">{q.service_type}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {bidView === 'dashboard' && (
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#ECE5DD]" data-testid="wa-transporter-dashboard">
              <div className="flex items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  <BarChart3 className="w-4 h-4 text-[#075E54]" />
                  {t('wa.myDashboard', 'My Dashboard')}
                </h4>
                <button
                  type="button"
                  onClick={() => setBidView(null)}
                  data-testid="wa-dashboard-close-btn"
                  className="text-xs text-gray-500 hover:text-gray-800"
                >
                  × {t('chat.bid.close', 'Close')}
                </button>
              </div>
              {trDashLoading && (
                <div className="flex items-center justify-center py-6 text-gray-500 text-xs gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t('wa.txLoading', 'Loading…')}
                </div>
              )}
              {!trDashLoading && !trDash && (
                <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {t('wa.txLoadError', 'Could not load your dashboard. Please try again.')}
                </div>
              )}
              {!trDashLoading && trDash && (
                <>
                  <div className="rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-4 shadow-sm">
                    <p className="text-emerald-100 text-[11px] uppercase tracking-wide">{t('transporter.receivedThisMonth', 'Received this month')}</p>
                    <p className="text-2xl font-bold" data-testid="wa-dash-earnings-month">{Math.round(trDash.earnings_this_month)} MAD</p>
                    <p className="text-emerald-100 text-[11px] mt-1">{t('transporter.allTime', 'All-time')}: {Math.round(trDash.earnings_all_time)} MAD</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { k: 'available_requests', label: t('transporter.availableRequests', 'Open requests'), color: 'text-amber-600' },
                      { k: 'quotes_sent', label: t('transporter.quoteSent', 'Quotes sent'), color: 'text-blue-600' },
                      { k: 'pending_response', label: t('transporter.pendingResponse', 'Pending response'), color: 'text-yellow-600' },
                      { k: 'active_bookings', label: t('transporter.activeBookings', 'Active bookings'), color: 'text-emerald-600' },
                    ].map((c) => (
                      <div key={c.k} className="rounded-xl border border-black/10 bg-white p-3 text-center shadow-sm" data-testid={`wa-dash-${c.k}`}>
                        <p className={`text-2xl font-bold ${c.color}`}>{trDash[c.k]}</p>
                        <p className="text-gray-500 text-[11px] leading-tight mt-0.5">{c.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl border border-amber-500/30 bg-amber-50 p-3 text-xs text-amber-800">
                    ⏳ {t('transporter.awaitingPayment', 'Awaiting payment')}: <strong>{Math.round(trDash.earnings_pending)} MAD</strong>
                    {' · '}💳 {trDash.paid_bookings} {t('transporter.completed', 'paid')}
                  </div>
                  <div className="flex items-center gap-3 pt-1">
                    <button type="button" onClick={openBidPicker} data-testid="wa-dash-goto-browse" className="text-xs font-semibold text-[#075E54] hover:text-[#25D366] flex items-center gap-1">
                      <Briefcase className="w-3.5 h-3.5" /> {t('chat.bid.browseRequests', 'Browse open requests')}
                    </button>
                    <button type="button" onClick={() => { onClose && onClose(); navigate('/drivers/dashboard'); }} className="text-xs font-semibold text-[#075E54] hover:text-[#25D366] flex items-center gap-1">
                      <ExternalLink className="w-3.5 h-3.5" /> {t('chat.cta.transporterDashboard', 'Full dashboard')}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

        </>
      )}

      {/* === Customer dashboard (authenticated My Dashboard) === */}
      {showCustomerDashboard && (
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-[#ECE5DD]" data-testid="wa-customer-dashboard">
          <div className="flex items-center gap-2 text-gray-700 text-sm mb-1">
            <BarChart3 className="w-4 h-4 text-[#075E54]" />
            <span className="font-medium truncate">{customer?.first_name || customer?.email}</span>
          </div>
          {custDashLoading && (
            <div className="flex items-center justify-center py-6 text-gray-500 text-xs gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('wa.txLoading', 'Loading…')}
            </div>
          )}
          {!custDashLoading && !custDash && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {t('wa.txLoadError', 'Could not load your dashboard. Please try again.')}
            </div>
          )}
          {!custDashLoading && custDash && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: 'total_requests', label: t('customer.totalRequests', 'Total requests'), color: 'text-gray-900' },
                  { k: 'pending', label: t('customer.pending', 'Pending'), color: 'text-amber-600' },
                  { k: 'have_quotes', label: t('customer.haveQuotes', 'Have quotes'), color: 'text-blue-600' },
                  { k: 'my_bookings', label: t('customer.myBookings', 'My bookings'), color: 'text-emerald-600' },
                ].map((c) => (
                  <div key={c.k} className="rounded-xl border border-black/10 bg-white p-4 text-center shadow-sm" data-testid={`wa-custdash-${c.k}`}>
                    <p className={`text-3xl font-bold ${c.color}`}>{custDash[c.k]}</p>
                    <p className="text-gray-500 text-[11px] leading-tight mt-1">{c.label}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-1">
                <button type="button" onClick={() => pickOption({ intent: 'get_quote', emoji: '📝', label: 'Get a Quote' })} data-testid="wa-custdash-new-quote" className="text-xs font-semibold text-[#075E54] hover:text-[#25D366] flex items-center gap-1">
                  📝 {t('chat.modes.getQuote', 'Get a Quote')}
                </button>
                <button type="button" onClick={() => { onClose && onClose(); navigate('/dashboard'); }} data-testid="wa-custdash-full" className="text-xs font-semibold text-[#075E54] hover:text-[#25D366] flex items-center gap-1">
                  <ExternalLink className="w-3.5 h-3.5" /> {t('wa.fullDashboard', 'Full dashboard')}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* === Normal WhatsApp conversation (track_booking / general / menu) === */}
      {!showLogin && !showTripForm && !showTransporter && !showCustomerDashboard && (
        <>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2 bg-[#ECE5DD]" data-testid="wa-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm whitespace-pre-wrap shadow-sm ${
                  m.role === 'user'
                    ? 'bg-[#DCF8C6] text-gray-900 rounded-tr-none'
                    : 'bg-white text-gray-900 rounded-tl-none'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}

            {showOptions && !sending && (
              <div className="pt-1 space-y-2" data-testid="wa-options">
                {options.map((opt) => (
                  <button
                    key={opt.intent}
                    onClick={() => pickOption(opt)}
                    data-testid={`wa-option-${opt.intent}`}
                    className="w-full text-left bg-white hover:bg-emerald-50 border border-[#25D366]/40 text-gray-800 text-sm font-medium rounded-lg px-3 py-2.5 shadow-sm transition-colors flex items-center gap-2"
                  >
                    <span className="text-base">{opt.emoji}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {sending && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-500 text-sm px-3 py-2 rounded-lg rounded-tl-none shadow-sm">…</div>
              </div>
            )}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="px-3 py-3 bg-[#F0F0F0] flex items-center gap-2">
            {voiceSupported && (
              <button
                type="button"
                onClick={toggleMic}
                data-testid="wa-mic-btn"
                aria-label={listening ? 'Stop voice input' : 'Speak'}
                title={listening ? t('wa.listening', 'Listening… tap to stop') : t('wa.speak', 'Speak')}
                className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                  listening ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-white text-[#075E54] border border-black/10 hover:bg-emerald-50'
                }`}
              >
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
            )}
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={listening ? t('wa.listening', 'Listening…') : t('wa.inputPlaceholder', 'Type a message')}
              disabled={sending}
              data-testid="wa-input"
              className="flex-1 bg-white border border-black/10 focus:border-[#25D366] focus:outline-none rounded-full px-4 py-2 text-gray-900 text-sm placeholder:text-gray-400"
              maxLength={2000}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="wa-send-btn"
              className="w-10 h-10 rounded-full bg-[#25D366] hover:bg-[#20bd5a] disabled:opacity-40 text-white flex items-center justify-center transition-colors flex-shrink-0"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default WhatsAppPanel;
